import type { ReactNode } from "react";
import { Link } from "react-router";
import { Compass } from "lucide-react";
import {
  BUTTON_BASE,
  BUTTON_VARIANT,
  buttonClass,
  type ButtonVariant,
} from "@/design/buttons";

/**
 * The form pieces the static web app needs and the shared kit could not carry
 * (R22 T11).
 *
 * Everything visual lives in `@/design/ui` and is imported by both frontends.
 * What is here is the handful of pieces that depend on **how this app
 * submits**: with no form action there is no `useFormStatus`, so pending is a
 * prop, and links are the router's rather than Next's.
 */

/**
 * Submit, with the pending label the screen docs specify.
 *
 * The Next host read pending from `useFormStatus`, which only reports on a form
 * action. Here the caller owns it (`useForm`), which is more honest anyway: the
 * component that knows a request is in flight is the one that started it.
 */
export function SubmitButton({
  children,
  pendingLabel,
  pending,
  variant = "primary",
  full = true,
  onClick,
}: {
  children: ReactNode;
  pendingLabel: string;
  pending: boolean;
  variant?: ButtonVariant;
  full?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type={onClick ? "button" : "submit"}
      disabled={pending}
      onClick={onClick}
      className={`${BUTTON_BASE} ${BUTTON_VARIANT[variant]} ${full ? "w-full" : ""}`}
    >
      {pending ? pendingLabel : children}
    </button>
  );
}

/**
 * The heading block every auth screen shares: a title and one line saying what
 * happens next. Kept because a flow that changes type weight halfway through
 * reads as two different products.
 */
export function AuthHeading({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="mb-6">
      <h1 className="text-2xl font-extrabold text-ink">{title}</h1>
      <p className="mt-1 text-sm text-muted">{sub}</p>
    </div>
  );
}

/**
 * The one-time code, as a single wide field rather than six boxes.
 *
 * Six boxes is the expected look and the wrong trade. A single input is what
 * `autocomplete="one-time-code"` actually fills from a message on iOS and
 * Android; split inputs routinely receive the whole code in the first box and
 * drop the rest, which turns the fastest path through this screen into the most
 * broken one. Paste, backspace across a boundary and screen-reader
 * announcement are all free here and all bespoke there.
 *
 * So it stays one field and does the work in styling: large, centred,
 * monospaced, widely tracked, digits-only as you type.
 */
export function CodeField({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (value: string) => void;
  error?: string;
}) {
  return (
    <div className="mb-4">
      <label
        htmlFor="code"
        className="mb-1.5 block text-center text-sm font-semibold text-ink"
      >
        Verification code
      </label>
      <input
        id="code"
        name="code"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        maxLength={6}
        placeholder="000000"
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? "code-error" : undefined}
        className={`w-full rounded-xl border bg-surface py-4 text-center font-mono text-3xl font-bold tracking-[0.4em] text-ink outline-none transition placeholder:text-muted/40 focus:border-accent ${
          error ? "border-danger" : "border-line"
        }`}
      />
      {error && (
        <p id="code-error" className="mt-1.5 text-center text-sm text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

/** "Didn't get a code? Resend", with the resend's own pending state. */
export function ResendRow({ children }: { children: ReactNode }) {
  return (
    <div className="mt-5 border-t border-line pt-5 text-center">
      <p className="text-sm text-muted">Didn&apos;t get a code?</p>
      {children}
    </div>
  );
}

/**
 * The way out of the auth flow without an account.
 *
 * Browsing Bookr never required a session: the landing page, search and the
 * business profiles are all public, and only booking needs an account. The auth
 * screens were the one place that did not say so, and a form is read as a wall
 * unless something beside it says otherwise.
 *
 * It sits last, under a rule, because it is the exit rather than the goal. The
 * helper line is the honest half of the offer: browsing is free and booking is
 * not, so somebody who came here to book does not take the exit and meet the
 * same requirement one screen later.
 */
export function BrowseAsGuest() {
  return (
    <div className="mt-6 border-t border-line pt-5">
      <Link to="/" className={buttonClass("secondary", "w-full")}>
        <Compass className="size-4" aria-hidden />
        Continue as a guest
      </Link>
      <p className="mt-2 text-center text-sm text-muted">
        Search and browse without an account. Booking needs one.
      </p>
    </div>
  );
}

/** A quiet link under a form ("New here? Sign up"). */
export function FootLink({
  lead,
  to,
  children,
  className,
}: {
  lead?: string;
  to: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <p className={`text-center text-sm text-muted ${className ?? "mt-2"}`}>
      {lead ? `${lead} ` : null}
      <Link to={to} className="font-semibold text-accent hover:underline">
        {children}
      </Link>
    </p>
  );
}

